import { supabase } from '../supabase';

/**
 * Get user profile by user ID
 */
export const getUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Get current user's profile
 */
export const getCurrentUserProfile = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No authenticated user');

  return getUserProfile(user.id);
};

/**
 * Enrich profile created by the auth trigger (company name, etc.).
 * Profile row is inserted server-side on signup; client only updates when authenticated.
 */
export const createUserProfile = async (userId, profileData) => {
  const updates = {
    full_name: profileData.full_name,
    updated_at: new Date().toISOString(),
  };

  if (profileData.company_name != null) {
    updates.company_name = profileData.company_name;
  }

  if (profileData.preferred_locale != null) {
    updates.preferred_locale = profileData.preferred_locale;
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update user profile
 */
export const updateUserProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('user_profiles')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update subscription plan
 */
export const updateSubscriptionPlan = async (userId, plan) => {
  const planLimits = {
    essential: { max_candidates: 1000, daily_limit: 75 },
    pro: { max_candidates: 5000, daily_limit: 250 },
    'essential-and-voice': { max_candidates: 1000, daily_limit: 75 },
    'pro-and-voice': { max_candidates: 5000, daily_limit: 250 },
    trial: { max_candidates: 25, daily_limit: 250 },
  };

  const limits = planLimits[plan] || planLimits.essential;

  return updateUserProfile(userId, {
    subscription_plan: plan,
    subscription_status: 'active',
    ...limits,
  });
};

/**
 * Update Calendly API key (legacy — prefer updateBookingIntegration)
 */
export const updateCalendlyKey = async (userId, apiKey) => {
  return updateUserProfile(userId, {
    booking_type: 'calendly',
    calendly_api_key: apiKey,
  });
};

/**
 * Save credentials for one booking provider only. Does not change booking_type
 * and does not clear other providers' stored credentials.
 */
export const saveBookingCredentials = async (userId, payload) => {
  const { bookingType, calendly, google, teams } = payload;
  const updates = {};

  if (bookingType === 'calendly') {
    updates.calendly_api_key = calendly?.apiKey?.trim() || null;
  } else if (bookingType === 'google' && google) {
    if ('accessToken' in google) updates.google_calendar_access_token = google.accessToken?.trim() || null;
    if ('clientId' in google) updates.google_calendar_client_id = google.clientId?.trim() || null;
    if ('refreshCode' in google) updates.google_calendar_refresh_code = google.refreshCode?.trim() || null;
    if ('clientSecret' in google) updates.google_calendar_client_secret = google.clientSecret?.trim() || null;
  } else if (bookingType === 'teams' && teams) {
    if ('accessToken' in teams) updates.teams_access_token = teams.accessToken?.trim() || null;
    if ('clientId' in teams) updates.teams_client_id = teams.clientId?.trim() || null;
    if ('clientSecret' in teams) updates.teams_client_secret = teams.clientSecret?.trim() || null;
    if ('refreshToken' in teams) updates.teams_refresh_token = teams.refreshToken?.trim() || null;
  }

  return updateUserProfile(userId, updates);
};

/**
 * Set which booking provider is active (booking_type). Credentials for all
 * providers remain stored; use saveBookingCredentials to update them.
 */
export const activateBookingProvider = async (userId, bookingType) => {
  return updateUserProfile(userId, { booking_type: bookingType });
};

/**
 * Remove all stored credentials for one booking provider. If that provider is
 * currently active (booking_type), booking_type is cleared as well.
 */
export const clearBookingCredentials = async (userId, bookingType, activeBookingType) => {
  const updates = {};

  if (bookingType === 'calendly') {
    updates.calendly_api_key = null;
  } else if (bookingType === 'google') {
    updates.google_calendar_access_token = null;
    updates.google_calendar_client_id = null;
    updates.google_calendar_refresh_code = null;
    updates.google_calendar_client_secret = null;
  } else if (bookingType === 'teams') {
    updates.teams_access_token = null;
    updates.teams_client_id = null;
    updates.teams_client_secret = null;
    updates.teams_refresh_token = null;
  }

  if (activeBookingType === bookingType) {
    updates.booking_type = null;
  }

  return updateUserProfile(userId, updates);
};

/** @deprecated Use saveBookingCredentials + activateBookingProvider */
export const updateBookingIntegration = saveBookingCredentials;

/**
 * Update Zoho credentials (client ID, client secret, refresh token)
 */
export const updateZohoCredentials = async (userId, credentials) => {
  return updateUserProfile(userId, {
    zoho_client_id: credentials.clientId ?? null,
    zoho_client_secret: credentials.clientSecret ?? null,
    zoho_refresh_token: credentials.refreshToken ?? null,
  });
};
